import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Check, Info, Copy } from "lucide-react";
import axios from "axios";
import confetti from "canvas-confetti";
import { PAYMENT } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

// pass:false answers screen the user out (honest, automated — not medical advice)
const SCREEN = [
  { key: "age", q: "First — are you 18 or older?", hint: "This program is for adults only.", opts: [["Yes, I'm 18 or older", true], ["No, I'm under 18", false]] },
  { key: "pregnancy", q: "Are you pregnant, breastfeeding, or planning a pregnancy in the next 6 months?", hint: "GLP-1 support isn't suitable during this time.", opts: [["No", true], ["Yes / planning to", false]] },
  { key: "thyroid", q: "Do you — or a close family member — have a history of medullary thyroid cancer or MEN2?", hint: "This is a known contraindication.", opts: [["No / not that I know of", true], ["Yes", false]] },
  { key: "pancreatitis", q: "Have you ever been diagnosed with pancreatitis?", hint: "We screen for this for your safety.", opts: [["No", true], ["Yes", false]] },
  { key: "allergy", q: "Have you ever had an allergic reaction to a GLP-1 medication?", hint: "", opts: [["No", true], ["Yes", false]] },
  { key: "goal", q: "What's your main goal?", hint: "This just helps us support you.", opts: [["Lose weight", true], ["Manage appetite", true], ["Metabolic support", true]] },
  { key: "weight", q: "Roughly, what's your current weight?", hint: "Helps us tailor your check-ins. No exact number needed.", opts: [["Under 150 lb", true], ["150–200 lb", true], ["200–250 lb", true], ["Over 250 lb", true]] },
];

// Suggested monthly dose based on GLP-1 history. Price is flat regardless of dose.
const DOSE = {
  Tirzepatide: {
    start: "10mg",
    prompt: "What is your current weekly Tirzepatide dose?",
    map: {
      "TR-2.5mg": "20mg",
      "TR-5mg": "30mg",
      "TR-7.5mg": "40mg",
      "TR-10mg": "50mg",
      "TR-12.5mg": "60mg",
      "TR-15mg": "100mg",
      "Not sure": "20mg",
    },
    tiers: ["20mg", "30mg", "40mg", "50mg", "60mg", "100mg"],
  },
  Semaglutide: {
    start: "1mg",
    prompt: "What is your current weekly Semaglutide dose?",
    map: {
      "S-0.25mg": "2mg",
      "S-0.5mg": "4mg",
      "S-1.0mg": "8mg",
      "S-1.7mg": "12mg",
      "S-2.0mg": "16mg",
      "S-2.4mg": "20mg",
      "Not sure": "4mg",
    },
    tiers: ["2mg", "4mg", "8mg", "12mg", "16mg", "20mg"],
  },
};

const nextVial = (tiers, vial) => {
  const idx = tiers.indexOf(vial);
  return idx >= 0 && idx < tiers.length - 1 ? tiers[idx + 1] : vial;
};

// Vial photo whose printed label matches the recommended dose (code shown, not the raw dose text).
const vialImageFor = (compound, dose, fallback) => {
  const n = parseInt(dose, 10);
  if (!n) return fallback;
  if (compound === "Tirzepatide") return `/uploads/vial_tr${n}.jpg`;
  if (compound === "Semaglutide") return `/uploads/vial_s${n}.jpg`;
  return fallback;
};

function CopyHandle({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {
        console.warn("Copy not permitted in this context:", value);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button onClick={copy} className="w-full flex items-center justify-between py-1.5 group" data-testid={testId}>
      <span className="text-[var(--ink)]/50">{label}</span>
      <span className="flex items-center gap-2 font-medium text-[var(--ink)]">
        {value}
        {copied ? <Check size={14} className="text-[var(--terra)]" /> : <Copy size={14} className="text-[var(--ink)]/35 group-hover:text-[var(--ink)] transition-colors" />}
      </span>
    </button>
  );
}

export default function Glp1Questionnaire({ open, plan, onClose, onEligible }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState([]); // booleans (true = pass)
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // "eligible" | "ineligible"

  const compoundKey = plan?.name === "Semaglutide" ? "Semaglutide" : "Tirzepatide";
  const dose = DOSE[compoundKey];
  const usedYes = answers["glp_history"] === "Yes, I've used one before";
  const appetiteInadequate = answers["appetite"] === "No, not quite";
  const wantsStepUp = appetiteInadequate && answers["step_up"] === "Yes, please adjust it";
  const questions = [
    ...SCREEN,
    { key: "glp_history", q: "Have you used a GLP-1 medication before?", hint: "So we can suggest the right starting point.", opts: [["No, I'm new to GLP-1", true], ["Yes, I've used one before", true]] },
    ...(usedYes ? [
      { key: "prior_dose", q: dose.prompt, hint: "This helps us tailor your plan — your price stays flat.", opts: Object.keys(dose.map).map((k) => [k, true]) },
      { key: "appetite", q: "Are you getting adequate appetite suppression at your current dose?", hint: "There's no wrong answer — this just helps us support you.", opts: [["Yes, it's working well", true], ["No, not quite", true]] },
    ] : []),
    ...(usedYes && appetiteInadequate ? [
      { key: "step_up", q: "Would you like us to fine-tune your plan to better support your goals?", hint: "We'll review your answers and confirm what's right for you.", opts: [["Yes, please adjust it", true], ["No, keep it the same", true]] },
    ] : []),
  ];
  const total = questions.length;
  const baseVial = usedYes ? (dose.map[answers["prior_dose"]] || dose.start) : dose.start;
  const recommendedDose = wantsStepUp ? nextVial(dose.tiers, baseVial) : baseVial;

  const phase = step < total ? "quiz" : step === total ? "email" : "result";
  const eligible = flags.every((f) => f !== false);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0); setAnswers({}); setFlags([]); setEmail(""); setOutcome(null); setSubmitting(false);
      }, 350);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (outcome === "eligible") {
      const fire = (opts) => confetti({ particleCount: 90, spread: 78, origin: { y: 0.55 }, colors: ["#bd7a5e", "#1f9e96", "#1a1d1a", "#f4f2ec"], ...opts });
      fire({});
      const t1 = setTimeout(() => fire({ angle: 60, origin: { x: 0, y: 0.6 } }), 180);
      const t2 = setTimeout(() => fire({ angle: 120, origin: { x: 1, y: 0.6 } }), 320);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [outcome]);

  const pick = (cur, label, pass) => {
    setAnswers((a) => ({ ...a, [cur.key]: label }));
    setFlags((f) => {
      const next = [...f];
      next[step] = pass;
      return next;
    });
    setTimeout(() => setStep((s) => s + 1), 240);
  };

  const submit = async () => {
    setSubmitting(true);
    const result = eligible ? "eligible" : "ineligible";
    try {
      await axios.post(`${API}/glp1-intake`, {
        compound: plan?.name || "GLP-1",
        answers: { ...answers, recommended_dose: recommendedDose },
        eligible,
        email: email || null,
      });
    } catch (e) {
      console.warn("GLP-1 lead capture failed (best-effort):", e);
    } finally {
      setSubmitting(false);
      setOutcome(result);
      setStep((s) => s + 1);
    }
  };

  const progress = Math.min(step / (total + 1), 1);
  const emailValid = /\S+@\S+\.\S+/.test(email);

  return (
    <AnimatePresence>
      {open && plan && (
        <motion.div
          className="fixed inset-0 z-[10001] flex flex-col bg-white text-[var(--ink)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="glp1-questionnaire"
        >
          {/* Sticky header */}
          <div className="flex items-center justify-between px-5 sm:px-8 h-16 sm:h-20 border-b border-[var(--ink)]/10 shrink-0">
            <span className="font-display text-2xl">
              {plan.code || plan.name} <span className="text-[var(--terra)]">screening</span>
            </span>
            <span className="hidden sm:block font-mono text-xs tracking-[0.2em] uppercase text-[var(--ink)]/50" data-testid="glp1-step">
              {phase === "quiz" ? `Step ${String(step + 1).padStart(2, "0")}/${String(total).padStart(2, "0")}` : phase === "email" ? "Almost done" : "Result"}
            </span>
            <button onClick={onClose} className="w-11 h-11 rounded-full border border-[var(--ink)]/15 flex items-center justify-center hover:bg-[var(--ink)]/5 transition-colors" data-testid="glp1-close" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="h-1 bg-[var(--ink)]/10 shrink-0">
            <motion.div className="h-full bg-[var(--terra)]" animate={{ width: `${(outcome ? 1 : progress) * 100}%` }} transition={{ ease: EASE }} />
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto w-full px-5 sm:px-8 py-14 sm:py-24 min-h-full flex flex-col justify-center">
              <AnimatePresence mode="wait">
                {phase === "quiz" && (
                  <motion.div key={step} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.45, ease: EASE }}>
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--terra)]">{String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
                    <h3 className="font-display text-4xl sm:text-5xl leading-[0.98] tracking-tight mt-5">{questions[step].q}</h3>
                    {questions[step].hint && <p className="font-body text-[var(--ink)]/55 mt-4">{questions[step].hint}</p>}
                    <div className="grid gap-3 mt-10">
                      {questions[step].opts.map(([label, pass], oi) => (
                        <button
                          key={label}
                          onClick={() => pick(questions[step], label, pass)}
                          className={`text-left font-body text-lg px-6 py-5 border transition-colors duration-200 ${
                            answers[questions[step].key] === label
                              ? "border-[var(--terra)] bg-[var(--terra)]/5 text-[var(--terra)]"
                              : "border-[var(--ink)]/15 hover:border-[var(--terra)]"
                          }`}
                          data-testid={`glp1-opt-${step}-${oi}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {phase === "email" && (
                  <motion.div key="email" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.45, ease: EASE }}>
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--terra)]">Last step</span>
                    <h3 className="font-display text-4xl sm:text-5xl leading-[0.98] tracking-tight mt-5">Where should we send your result?</h3>
                    <p className="font-body text-[var(--ink)]/55 mt-4">We'll review your answers and confirm your eligibility. No spam.</p>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="mt-10 w-full bg-transparent border-b border-[var(--ink)]/25 focus:border-[var(--terra)] outline-none py-4 font-body text-2xl placeholder:text-[var(--ink)]/30 transition-colors duration-200"
                      data-testid="glp1-email"
                    />
                    <button
                      onClick={submit}
                      disabled={!emailValid || submitting}
                      className="mt-10 inline-flex items-center gap-2 bg-[var(--terra)] text-white px-8 py-4 rounded-full disabled:opacity-40 transition-opacity"
                      data-testid="glp1-submit"
                    >
                      {submitting ? "Reviewing…" : "See my result"} <ArrowRight size={16} />
                    </button>
                  </motion.div>
                )}

                {phase === "result" && outcome === "eligible" && (
                  <motion.div key="ok" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="text-center" data-testid="glp1-eligible">
                    <div className="mx-auto w-16 h-16 rounded-full bg-[var(--terra)] text-white flex items-center justify-center">
                      <Check size={28} />
                    </div>
                    <p className="font-mono text-xs tracking-[0.25em] uppercase text-[var(--terra)] mt-8">Congratulations</p>
                    <h3 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-3">You're eligible!</h3>
                    <div className="mt-6 mx-auto w-48 h-48 rounded-2xl overflow-hidden border border-[var(--line)] bg-white flex items-center justify-center">
                      <img
                        src={vialImageFor(compoundKey, recommendedDose, plan.img)}
                        alt="Your matched research vial"
                        className="w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.src = plan.img; }}
                        data-testid="glp1-matched-vial"
                      />
                    </div>
                    <p className="font-body text-xs text-[var(--ink)]/45 mt-3">This is the vial we'll prepare for you.</p>
                    <p className="font-body text-[var(--ink)]/60 mt-5 max-w-md mx-auto leading-relaxed">
                      Based on your answers, {plan.code || plan.name} looks like a fit. Your flat <b>${plan.price}/month</b> — free shipping, cancel anytime — is ready to order.
                    </p>
                    <p className="font-body text-xs text-[var(--ink)]/45 mt-4 max-w-sm mx-auto">We'll confirm the right monthly dose with you before shipping. Your price never changes as your dose is adjusted.</p>
                    <button
                      onClick={() => onEligible(plan, recommendedDose)}
                      className="mt-8 bg-[var(--ink)] text-[var(--bone)] px-10 py-5 rounded-full inline-flex items-center gap-2 text-lg font-medium hover:scale-[1.03] transition-transform"
                      data-testid="glp1-continue-checkout"
                    >
                      Order now <ArrowRight size={18} />
                    </button>
                    <div className="mt-8 max-w-sm mx-auto border border-[var(--ink)]/12 rounded-2xl p-5 bg-[var(--ink)]/[0.02]" data-testid="glp1-payment-reminder">
                      <p className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--ink)]/50 mb-3">How to pay</p>
                      <p className="font-body text-sm text-[var(--ink)]/70 leading-relaxed">
                        After you place your order, tap a handle to copy it, then send your total:
                      </p>
                      <div className="mt-3 space-y-0.5 font-body text-sm">
                        <CopyHandle label="Venmo" value={PAYMENT.venmo} testId="glp1-pay-venmo" />
                        <CopyHandle label="Cash App" value={PAYMENT.cashapp} testId="glp1-pay-cashapp" />
                        <CopyHandle label="Chime" value={PAYMENT.chime} testId="glp1-pay-chime" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {phase === "result" && outcome === "ineligible" && (
                  <motion.div key="no" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="text-center" data-testid="glp1-ineligible">
                    <div className="mx-auto w-16 h-16 rounded-full border border-[var(--ink)]/15 flex items-center justify-center">
                      <Info size={26} className="text-[var(--ink)]/60" />
                    </div>
                    <h3 className="font-display text-4xl sm:text-5xl leading-[0.98] tracking-tight mt-8">This isn't a match right now.</h3>
                    <p className="font-body text-[var(--ink)]/60 mt-5 max-w-md mx-auto leading-relaxed">
                      Based on your answers, this program isn't the right fit for you today. This is an automated eligibility screen — we're not a medical provider. We've emailed you the details, and our support team is happy to help if something changes.
                    </p>
                    <button onClick={onClose} className="mt-10 border border-[var(--ink)]/20 px-8 py-4 rounded-full hover:bg-[var(--ink)]/5 transition-colors" data-testid="glp1-ineligible-close">
                      Close
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
